import { all, call, put, select, take } from 'redux-saga/effects';
import * as actions from '../actions';
import {
  setDraftCount,
  setHasPostings,
  setHasPublishedPostings,
} from '../actions/PermanentJobs/PermanentJobs';
import { setFirebaseUserProperties, logCustomEvent } from '../api/analytics';
import history from '../browserHistory';
import { PermanentJobsService } from '../domain/PermanentJobs/services';
// Leaf service, not the domain index: the index also exports the token client,
// which drags the API/redux graph into a module that only touches the widget.
import { retireSaltclickChatOnLogout } from '../domain/SaltclickChat/services/retireSaltclickChatOnLogout';
import { growthbook, shouldUseDsoStructure } from '../growthbook';
import { apiMethodsConst } from '../services/methods';
import { clearDatadogUser, setDatadogUser } from '../services/datadog';
import { PosthogService } from '../services/posthog';
import { AutoNavigateActionNeeded, SignUpRedesign } from '../constants/ff.posthog';
import { dashboardJobsService } from '../domain/DashboardJobs/dashboardJobsService';
import { extractReferralCodeFromUrl } from '../domain/Referral/referralCode';
import { store } from '../store/configureStore';
import { clearPosthogIdentifyCache, posthogIdentifyIfChanged } from '../utils/posthog/identify';
import {
  ACTION_NEEDED_PATH,
  DASHBOARD_PATH,
  shouldAutoNavigateToActionNeeded,
} from './helpers/shouldAutoNavigateToActionNeeded';
import { callAndDispatch } from './utils';

export function* watchInitialLoading() {
  const events = actions.createRequestTypes(actions.INITIAL_LOADING);

  while (true) {
    yield take(actions.INITIAL_LOADING);

    yield put({ type: events.REQUEST });

    // Sagas live outside React — read PostHog directly to gate legacy
    // hard-navigation paths (same pattern as growthbook's getShould* flags).
    const isSignUpRedesignOn = PosthogService.getInstance().isFeatureFlagVariant(
      SignUpRedesign,
      'test',
    );

    // Read before any await: by the time boot finishes, the referral route
    // element may already have replaced the URL.
    const isBootOnReferralLink =
      extractReferralCodeFromUrl(`${history.location?.pathname}${history.location?.search}`) !==
      null;

    try {
      if (!localStorage.getItem('refresh_token_dental')) {
        throw new Error('No refresh token');
      }

      yield* callAndDispatch(apiMethodsConst.AUTH_ME_REQUEST);

      const user = yield select((state) => state.user.user);

      if (user && user.id && user.user_type !== 'DNT') {
        throw new Error('HYG not allowed');
      }

      if (user && user.id && user.is_profile_complete) {
        // Get childOffices for PostHog identification
        const childOffices = yield select((state) => state.user.childOffices);

        // Set Datadog user context (identity — unaffected by the office strip)
        setDatadogUser(user);

        const selectedOfficeId = yield select((state) => state.user.selectedOfficeId);

        // GOTU-4961: read the selected/own office through the /office boundary for EVERY
        // account, not just multi-office (was `childOffices?.length > 1`). Single-office
        // accounts otherwise get their office subset only from authMeRequest (/auth/me),
        // which bypasses this boundary + the compile guard and would leave them office-less
        // once /auth/me is slimmed. officeId falls back to user.id (the account's own
        // office) when nothing is selected.
        yield all([
          callAndDispatch(
            apiMethodsConst.GET_SELECTED_OFFICE_PROFILE_REQUEST,
            selectedOfficeId ?? user?.id,
          ),
          callAndDispatch(apiMethodsConst.USER_CREDENTIALS_REQUEST),
          callAndDispatch(apiMethodsConst.GET_STATE_WORKER_CLASSIFICATION_REQUEST),
        ]);

        // GOTU-4961: identify PostHog + Firebase ONCE, AFTER the /office merge, sourcing office
        // props from the SELECTED office (selectedChildOffice for DSO — state.user.user stays
        // stripped there; else the own merged office). Deliberately NOT identified before the
        // office read: the strip (authMeRequest) and the /office read must see the SAME flag
        // value, which holds only if nothing re-identifies between them (identify changes the
        // distinct_id the flag buckets on). waitForFlagReload so later boot flag reads
        // (e.g. permanent-job-v2 below) evaluate against the identified user.
        const selectedOffice = yield select(
          (state) => state.user.selectedChildOffice ?? state.user.user,
        );
        const mergedUser = yield select((state) => state.user.user);
        const didIdentify = posthogIdentifyIfChanged(mergedUser, childOffices, selectedOffice);
        if (didIdentify) {
          yield call([PosthogService.getInstance(), 'waitForFlagReload']);
        }
        yield setFirebaseUserProperties(mergedUser, selectedOffice);

        // Pre-fetch hasPostings flag for instant ManageJobs redirect
        try {
          const postingsResult = yield call(PermanentJobsService.getJobPostings, {
            limit: 1,
            page: 1,
          });
          const stats = postingsResult.statistics.tabs;
          const hasAny =
            stats.active > 0 || stats.closed > 0 || stats.draft > 0 || stats.suspended > 0;
          const hasPublished = stats.active > 0 || stats.closed > 0 || stats.suspended > 0;
          yield put(setHasPostings(hasAny));
          yield put(setHasPublishedPostings(hasPublished));
          yield put(setDraftCount(stats.draft));
        } catch {
          // Silently fail — ManageJobs will fall back to its own loading
        }

        // Adjustments request
        const jobsWithAdjustments = store.getState().job.adjustments;

        if (jobsWithAdjustments.length !== 0) {
          history.push(`/dashboard/adjustments/${jobsWithAdjustments[0].id}`, {
            job_id: jobsWithAdjustments[0].id,
          });
        }

        // Experiment: guide offices straight to their Action Needed shifts on
        // login / app-open. Gated behind a flag; only fetch the count when it's
        // on. Runs after higher-priority redirects so it never overrides them
        // (the helper requires the user to still be on the dashboard).
        // Experiment: only the "test" variant auto-navigates; "control" (and
        // anyone not enrolled) keeps the default landing.
        const isAutoNavigateActionNeededOn = PosthogService.getInstance().isFeatureFlagVariant(
          AutoNavigateActionNeeded,
          'test',
        );

        if (isAutoNavigateActionNeededOn) {
          try {
            const dashboardJobsInfo = yield call(dashboardJobsService.getDashboardJobsInfo);

            if (
              shouldAutoNavigateToActionNeeded({
                isFlagEnabled: isAutoNavigateActionNeededOn,
                actionRequiredCount: dashboardJobsInfo?.pendingCount ?? 0,
                currentPath: history.location?.pathname,
              })
            ) {
              if (history.location?.pathname === DASHBOARD_PATH) {
                // App-open while logged in: already on the dashboard, take over now.
                history.push(ACTION_NEEDED_PATH);
              } else {
                // Fresh login: the post-login redirect lands on the dashboard a
                // tick later and would clobber an immediate push, so wait for it
                // and navigate then. The timeout drops the listener if the user
                // is routed elsewhere (e.g. incomplete profile -> sign-up).
                const stopListening = history.listen(({ location }) => {
                  if (location.pathname === DASHBOARD_PATH) {
                    stopListening();
                    history.push(ACTION_NEEDED_PATH);
                  }
                });
                setTimeout(() => stopListening(), 5000);
              }
            }
          } catch {
            // Silently fall back to the default landing page.
          }
        }
      }

      yield put({ type: events.SUCCESS });

      if (user && !user.is_profile_complete) {
        // Send incomplete-profile users back into sign-up.
        if (isSignUpRedesignOn) {
          // Hydrate WC state — step pages branch on it for the worker-type tab.
          yield* callAndDispatch(apiMethodsConst.GET_STATE_WORKER_CLASSIFICATION_REQUEST);
          // Always restart the wizard at Step 1 on refresh/boot. Mid-flow
          // navigation between steps still works because INITIAL_LOADING
          // only fires on app start, not on intra-app route changes.
          // GOTU-9922: except on a referral link, whose route element lands
          // on Step 1 itself carrying the dialog state — pushing here as well,
          // after the awaited call above, would replace that entry and drop it.
          if (!isBootOnReferralLink) {
            history.push('/signup/your-info');
          }
        } else {
          history.push('/registration');
        }
      }
    } catch (error) {
      const isSignUpPath = history.location?.pathname === '/signup';

      if (isSignUpPath) {
        const hasReferralId = history.location?.search?.includes('referral');

        if (hasReferralId) {
          history.push(`/${history.location?.search}`);
        } else {
          history.push('/');
        }
      }

      // GOTU-7432: a v2 referral link (`/referrals/v2/<CODE>` or
      // `/referral?referralCode=`) must reach its route element with the URL
      // intact — this push runs before any route renders, and `/` drops it.
      const isReferralLink =
        extractReferralCodeFromUrl(`${history.location?.pathname}${history.location?.search}`) !==
        null;

      if (
        history.location?.pathname !== '/signup' &&
        !history.location?.pathname?.includes('/forgotpassword') &&
        !history.location.search.includes('referral') &&
        !isReferralLink
      ) {
        history.push('/');
      }

      if (error?.message === 'HYG not allowed') {
        yield put({
          type: 'LOGOUT_ACTION_SUCCESS',
          payload: {
            isError: true,
            title: 'Sorry!',
            text: 'Only dental offices are allowed to use the Dental Portal. Please, download the GoTu mobile application.',
            buttonText: 'Close',
            action: 'LOGOUT_ACTION',
          },
        });
      }
      if (error.response?.data.message !== 'No refresh token') {
        yield put({ type: events.FAILURE, payload: error.response?.data });
      }
      yield put({ type: events.FAILURE });
    }
  }
}

export function* watchSignIn() {
  const events = actions.createRequestTypes(actions.SIGN_IN_ACTION);
  while (true) {
    const cred = yield take(actions.SIGN_IN_ACTION);

    yield put({ type: events.REQUEST });

    try {
      yield* callAndDispatch(apiMethodsConst.SIGN_IN_REQUEST, cred);

      yield logCustomEvent('sign_in', {
        email: cred.email,
      });

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

export function* watchSignUP() {
  const events = actions.createRequestTypes(actions.SIGN_UP_ACTION);
  while (true) {
    const cred = yield take(actions.SIGN_UP_ACTION);
    yield put({ type: events.REQUEST });

    try {
      yield* callAndDispatch(apiMethodsConst.AUTH_SIGN_UP_REQUEST, cred);
      yield* callAndDispatch(
        apiMethodsConst.AUTH_REFRESH_REQUEST,
        localStorage.getItem('refresh_token_dental'),
      );
      yield* callAndDispatch(apiMethodsConst.AUTH_ME_REQUEST);

      if (shouldUseDsoStructure()) {
        yield* callAndDispatch(apiMethodsConst.ACCEPT_TERMS_AND_CONDITIONS_FOR_STAFF_REQUEST);
        yield* callAndDispatch(apiMethodsConst.GET_TERMS_AND_CONDITIONS_FOR_STAFF_REQUEST);
      } else {
        yield* callAndDispatch(apiMethodsConst.ACCEPT_TERMS_REQUEST);
      }
      yield logCustomEvent('sign_up_started', {
        email: cred.email,
      });
      yield put({ type: events.SUCCESS });

      const user = yield select((state) => state.user.user);

      cred?.onSuccess?.(user);

      history.push('/registration');
    } catch (error) {
      localStorage.removeItem('refresh_token_dental');
      localStorage.removeItem('selectedOfficeId');
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

export function* watchAuthLogout() {
  const events = actions.createRequestTypes(actions.LOGOUT_ACTION);
  while (true) {
    // Only a forced sign-out (a blocked or paused account) carries a message; manual logout has none.
    const { message } = yield take(actions.LOGOUT_ACTION);
    yield put({ type: events.REQUEST });
    try {
      // Capture the id before the API call so we can purge this user's identify cache.
      const userId = yield select((state) => state.user?.user?.id);

      // Retire the chat BEFORE the logout request, and only here: closing the
      // conversation needs an identity token, that token is minted with the portal's
      // own access token, and the logout request is what invalidates it. Afterwards
      // there is no way left to close a conversation this account owns — and nobody
      // else can, since Salesforce answers 403 to a token that does not own it.
      //
      // The widget is init'd once per page load and outlives the React tree that
      // booted it, so without this the tab keeps an open conversation verified as the
      // account that just left. Ending only the session is not enough: the
      // conversation stays open, and the next login resumes it and lands on the ended
      // screen with no way forward (GOTU-5918).
      //
      // Not awaited: logout must not wait on Salesforce, and logout is an in-app
      // navigation, so the document survives to finish the requests. Only the mint
      // needs the access token; the scrt2 calls after it carry their own.
      retireSaltclickChatOnLogout();

      try {
        yield* callAndDispatch(apiMethodsConst.AUTH_LOGOUT_REQUEST);
      } catch (error) {
        // A forced sign-out leaves the app even when the server could not end the session:
        // the account can no longer use it either way.
        if (!message) {
          throw error;
        }
      }
      growthbook.setAttributes({});

      // Clear Datadog user context
      clearDatadogUser();

      // Reset PostHog identity so the next login does not merge Persons via the cookie's
      // residual distinct_id. Pair the cached-identify wipe so the next session re-identifies.
      PosthogService.getInstance().reset();
      clearPosthogIdentifyCache(userId);

      history.push('/');
      // The root reducer keeps this payload as the message the signed-out screen shows.
      yield put({
        type: events.SUCCESS,
        payload: message && { isError: true, title: 'Sorry!', text: message, buttonText: 'Close' },
      });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}
